import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/ensinoaluno.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const EnsinoAlunoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_ensino_aluno: '',
    id_tp_ensino: '',
    id_escola: '',
    ano_letivo: '',
    total_alunos: '',
    data_ultima_atualizacao: '',
    login_responsavel: '',
    repasse_financeiro: '',
    data_repasse: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [tiposEnsino, setTiposEnsino] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);
  const [proximoId, setProximoId] = useState('');

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
      escola.id_escola?.toString().includes(escolaSearch) ||
      escola.codigo_inep?.toString().includes(escolaSearch)
    );
  }, [escolas, escolaSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposEnsino = useCallback(async () => {
    try {
      const response = await api.get('/tipo-ensino');
      setTiposEnsino(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de ensino: ${error.message}`);
    }
  }, [mostrarMensagem]);

  // ID é gerado automaticamente no backend ao clicar em Adicionar
  const fetchNextId = useCallback(() => {
    // Não busca mais o próximo ID - será gerado atomicamente no backend
  }, []);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/ensino-aluno');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'ensino do aluno', { id, dadosAtualizacao });
    try {
      const validKeys = [
        'id_tp_ensino',
        'id_escola',
        'ano_letivo',
        'total_alunos',
        'data_ultima_atualizacao',
        'login_responsavel',
        'repasse_financeiro',
        'data_repasse',
      ];

      const dadosValidos = {};

      for (const key of validKeys) {
        if (dadosAtualizacao.hasOwnProperty(key)) {
          if (key === 'repasse_financeiro') {
            const valorString = String(dadosAtualizacao[key]);
            if (valorString === '' || dadosAtualizacao[key] === null || valorString === 'null') {
              dadosValidos[key] = null;
            } else if (valorString.includes(',')) {
              // Valor formatado brasileiro (ex: 1.000,00)
              dadosValidos[key] = parseFloat(valorString.replace(/\./g, '').replace(',', '.'));
            } else {
              // Valor simples
              dadosValidos[key] = parseFloat(dadosAtualizacao[key]);
            }
          } else {
            dadosValidos[key] = dadosAtualizacao[key] === '' ? null : dadosAtualizacao[key];
          }
        }
      }

      const response = await api.put(`/ensino-aluno/${id}`, dadosValidos);
      logarSucesso('Atualizar', 'ensino do aluno', response.data);
      if (response && response.data.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'ensino do aluno');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'ensino do aluno', { id });
    try {
      const response = await api.delete(`/ensino-aluno/${id}`);
      logarSucesso('Excluir', 'ensino do aluno', response.data);
      setDados(currentData => currentData.filter(item => item.id_ensino_aluno !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'ensino do aluno');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const escolasNomes = escolas.reduce((acc, escola) => {
      acc[escola.id_escola] = escola.nome_escola;
      return acc;
    }, {});

    const tiposEnsinoNomes = tiposEnsino.reduce((acc, tipo) => {
      acc[tipo.id_tp_ensino] = tipo.descr_tp_ensino;
      return acc;
    }, {});

    const colunasBase = [
      {
        title: 'ID Ensino Aluno',
        field: 'id_ensino_aluno',
        width: 180,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Tipo Ensino',
        field: 'id_tp_ensino',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar tipo ensino...',
        sorter: 'string',
        formatter: (cell) => tiposEnsinoNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeTipoEnsino = tiposEnsinoNomes[rowValue] || rowValue || '';
          return nomeTipoEnsino.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: tiposEnsinoNomes }
      },
      {
        title: 'Escola',
        field: 'id_escola',
        width: 300,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar escola...',
        sorter: 'string',
        formatter: (cell) => escolasNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeEscola = escolasNomes[rowValue] || rowValue || '';
          return nomeEscola.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: escolasNomes }
      },
      {
        title: 'Ano Letivo',
        field: 'ano_letivo',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ano...',
        sorter: 'number',
      },
      {
        title: 'Total Alunos',
        field: 'total_alunos',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar total...',
        sorter: 'number',
      },
      {
        title: 'Data da última Atualização',
        field: 'data_ultima_atualizacao',
        width: 300,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          try {
            const date = new Date(rowValue);
            if (isNaN(date.getTime())) return rowValue.includes(headerValue);

            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            return formattedDate.includes(headerValue);
          } catch (e) {
            return false;
          }
        },
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Login Responsável',
        field: 'login_responsavel',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar login...',
        sorter: 'string',
      },
      {
        title: 'Repasse Financeiro',
        field: 'repasse_financeiro',
        width: 230,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar repasse...',
        sorter: 'number',
        formatter: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined || value === '') {
            return '';
          }
          return parseFloat(value).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          });
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: function (cell, onRendered, success, cancel) {
          const input = document.createElement('input');
          input.type = 'text';
          input.style.width = '100%';
          input.style.padding = '4px';
          input.style.boxSizing = 'border-box';

          const currentValue = cell.getValue();
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }

          input.addEventListener('input', (e) => {
            let value = e.target.value.replace(/\D/g, '');
            if (value) {
              value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              e.target.value = value;
            }
          });

          onRendered(() => {
            input.focus();
            input.select();
          });

          const successFunc = () => {
            const rawValue = input.value.replace(/\./g, '').replace(',', '.');
            success(parseFloat(rawValue) || 0);
          };

          input.addEventListener('blur', successFunc);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              successFunc();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Data Repasse',
        width: 180,
        field: 'data_repasse',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          try {
            const date = new Date(rowValue);
            if (isNaN(date.getTime())) return rowValue.includes(headerValue);

            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            return formattedDate.includes(headerValue);
          } catch (e) {
            return false;
          }
        },
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_ensino_aluno, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_ensino_aluno}"?`)) {
            excluirRegistro(data.id_ensino_aluno);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: col.field === 'id_ensino_aluno' ? undefined : (col.editor || 'input')
      }))
    ];
  }, [escolas, tiposEnsino, atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_ensino_aluno'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/ensino-aluno');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchEscolas();
    fetchTiposEnsino();
    fetchNextId();
  }, [verificarAPI, fetchEscolas, fetchTiposEnsino, fetchNextId]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_ensino_aluno, id_tp_ensino, id_escola, ano_letivo, total_alunos, data_ultima_atualizacao, login_responsavel, repasse_financeiro, data_repasse } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    // ID é gerado automaticamente pelo backend - não precisa validar
    if (!id_tp_ensino) {
      camposObrigatorios.push('Tipo Ensino');
      erros.push('id_tp_ensino');
    }
    if (!id_escola) {
      camposObrigatorios.push('Escola');
      erros.push('id_escola');
    }
    if (!ano_letivo) {
      camposObrigatorios.push('Ano Letivo');
      erros.push('ano_letivo');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_ensino_aluno: 'AUTO', // ID será gerado automaticamente pelo backend
      id_tp_ensino: id_tp_ensino.trim(),
      id_escola: id_escola.trim(),
      ano_letivo: parseInt(ano_letivo),
      total_alunos: total_alunos ? parseInt(total_alunos) : null,
      data_ultima_atualizacao: data_ultima_atualizacao || null,
      login_responsavel: login_responsavel || null,
      repasse_financeiro: repasse_financeiro === '' ? null : parseFloat(repasse_financeiro.replace(/\./g, '').replace(',', '.')),
      data_repasse: data_repasse || null,
    };

    logarRequisicao('Adicionar', 'ensino do aluno', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/ensino-aluno', dataToSend);
      logarSucesso('Adicionar', 'ensino do aluno', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_ensino_aluno: '', id_tp_ensino: '', id_escola: '', ano_letivo: '', total_alunos: '', data_ultima_atualizacao: '', login_responsavel: '', repasse_financeiro: '', data_repasse: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      fetchNextId();
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'ensino do aluno');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'repasse_financeiro') {
      const apenasNumeros = value.replace(/\D/g, '');

      if (apenasNumeros === '') {
        setFormData(prev => ({ ...prev, [name]: '' }));
        return;
      }

      const numero = parseInt(apenasNumeros, 10);
      const valorFormatado = (numero / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      setFormData(prev => ({ ...prev, [name]: valorFormatado }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'ensino_aluno.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <img src={etgLogo} alt="ETG Logo" style={{ height: '80px' }} />
          <h1 style={{ textAlign: 'center', flexGrow: 1 }}>Gerenciador de Ensino Aluno</h1>
          <img src={ufmgLogo} alt="UFMG Logo" style={{ height: '80px' }} />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_ensino_aluno</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/ensino aluno.mov" buttonText="Ver Tutorial Ensino Aluno" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}


      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_ensino_aluno">ID Ensino aluno:</label>
            <input type="text" id="id_ensino_aluno" name="id_ensino_aluno" value="" placeholder="Gerado ao adicionar" disabled={true} style={{ backgroundColor: '#e9e9e9', cursor: 'not-allowed', color: '#888' }} />
          </div>
          <div className="form-group">
            <label htmlFor="id_tp_ensino">Tipo ensino:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select id="id_tp_ensino" name="id_tp_ensino" value={formData.id_tp_ensino} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_tp_ensino') ? 'input-error' : ''}>
              <option value="">Selecione um tipo de ensino</option>
              {tiposEnsino.map(ensino => (
                <option key={ensino.id_tp_ensino} value={ensino.id_tp_ensino}>{ensino.descr_tp_ensino}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="id_escola">Escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_escola"
              name="id_escola"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(esc => {
                  const formato = `${esc.nome_escola} (${esc.codigo_inep || esc.id_escola})`;
                  return formato === e.target.value || esc.nome_escola === e.target.value;
                });
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_escola'));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '' }));
                }
              }}
              list="escolas-list-ensinoaluno"
              placeholder="Digite para buscar escola..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_escola') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="escolas-list-ensinoaluno">
              {escolasFiltradas.map(escola => (
                <option key={escola.id_escola} value={`${escola.nome_escola} (${escola.codigo_inep || escola.id_escola})`} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="ano_letivo">Ano letivo:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="number" id="ano_letivo" name="ano_letivo" value={formData.ano_letivo} onChange={handleInputChange} placeholder="Ex: 2024" min="0" disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('ano_letivo') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="total_alunos">Total alunos:</label>
            <input type="number" id="total_alunos" name="total_alunos" value={formData.total_alunos} onChange={handleInputChange} placeholder="Ex: 100" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="data_ultima_atualizacao">Data última atualização:</label>
            <input type="date" id="data_ultima_atualizacao" name="data_ultima_atualizacao" value={formData.data_ultima_atualizacao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="login_responsavel">Login responsável:</label>
            <input type="text" id="login_responsavel" name="login_responsavel" value={formData.login_responsavel} onChange={handleInputChange} placeholder="Ex: usuario" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="repasse_financeiro">Repasse financeiro:</label>
            <input type="text" id="repasse_financeiro" name="repasse_financeiro" value={formData.repasse_financeiro} onChange={handleInputChange} placeholder="Ex: 1.000,00" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="data_repasse">Data repasse:</label>
            <input type="date" id="data_repasse" name="data_repasse" value={formData.data_repasse} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>

          <div className="actions-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}>
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>


      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default EnsinoAlunoManager;
